import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme, alpha } from "@mui/material/styles";
import { TuneOutlined } from "@mui/icons-material";
import { api } from "../../services/api";
import { useToast } from "../../components/ToastContext";
import SearchComponent from "../../components/Search";
import { filterRowsByColumns } from '../../Utils/gridSearch';
import StatusFilterHeader, { applyStatusFilter } from '../../components/StatusFilterHeader';
import SetupDialog from "../adminSettings/AddSetup";

const mapSetupDataToState = (setupData) => ({
  setupId: setupData.setupId,
  setupKey: setupData.setupKey || "",
  setupKeyValue: setupData.setupKeyValue || "",
  isActive: setupData.isActive || false,
  createdBy: setupData.createdBy,
  createdOn: setupData.createdOn,
  updatedBy: setupData.updatedBy,
  updatedOn: setupData.updatedOn,
});

export default function EnvSetup() {
  const theme = useTheme();
  const toast = useToast();

  const [setupDetails, setSetupDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState('all');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSetupData, setSelectedSetupData] = useState(null);

  const fetchSetupDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get("/Project/GetAllSetupDetails");
      setSetupDetails(response.data);
    } catch (error) {
      console.error("Error fetching setup details:", error);
      toast.error(error.message || "Failed to fetch setup details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetupDetails();
  }, []);

  const handleAddNew = () => {
    setSelectedSetupData(null);
    setIsDialogOpen(true);
  };

  const handleEdit = async (setupId) => {
    try {
      const response = await api.get(`/Project/GetSetupDetailsById/${setupId}`, {
        headers: { "X-CORRELATION-ID": "4fa84814-7ba6-4cbc-b762-c006aa8f57c4" },
      });
      if (response.data) {
        setSelectedSetupData(mapSetupDataToState(response.data));
        setIsDialogOpen(true);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch setup details for editing.");
    }
  };

  const columns = useMemo(() => [
    {
      field: "setupKey",
      headerName: "Setup Key",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography sx={{ py: 1.5, color: "text.primary"}}>
          {params.row?.setupKey || ""}
        </Typography>
      ),
    },
    {
      field: "setupKeyValue",
      headerName: "Setup Value",
      flex: 1.2,
      minWidth: 260,
      renderCell: (params) => (
        <Typography sx={{ py: 1.5, color: "text.secondary" }}>
          {params.row?.setupKeyValue || ""}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.7,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // Expose a searchable label so search matches "active"/"inactive"
      valueGetter: (value, row) => (row?.isActive ? "Active" : "Inactive"),
      renderHeader: () => (
        <StatusFilterHeader value={statusFilter} onChange={setStatusFilter} />
      ),
      renderCell: (params) => (
        <Box
          component="span"
          sx={{
            mt: 1.1,
            px: 1.4,
            py: 0.55,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            color: params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
            bgcolor: alpha(
              params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
              theme.palette.mode === "dark" ? 0.18 : 0.12
            ),
          }}
        >
          {params.row?.isActive ? "Active" : "Inactive"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEdit(params.row.setupId)}
          sx={{ mt: 0.9, borderRadius: 999, px: 1.8 }}
        >
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, statusFilter]);

  // Search across all data columns, then apply the Status dropdown filter
  const filteredSetups = useMemo(
    () => applyStatusFilter(filterRowsByColumns(setupDetails, searchTerm, columns), statusFilter),
    [setupDetails, searchTerm, columns, statusFilter]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1), color: theme.palette.primary.main }}>
            <TuneOutlined />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}>
            Environment Setup
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchComponent
            value={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search setup..."
            sx={{ mb: theme.spacing(2) }}
          />
          <Button
            variant="contained"
            onClick={handleAddNew}
            sx={{ borderRadius: 2, px: 2, whiteSpace: 'nowrap' }}
          >
            Add Setup
          </Button>
        </Box>
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: 'calc(100vh - 310px)', minHeight: 380, width: '100%' }}>
        {loading ? (
          <Box sx={{ minHeight: 280, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredSetups}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
            hideFooterSelectedRowCount
            getRowId={(row) => row.setupId?.toString() || row.id?.toString()}
            sx={{
              border: "none",
              bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#ffffff",

              "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root, & .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiSvgIcon-root, & .MuiCheckbox-root.Mui-indeterminate .MuiSvgIcon-root": {
                color: (t) => `${t.palette.primary.main} !important`,
              },

              "& .MuiCheckbox-root .MuiSvgIcon-root": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
              },

              "& .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root": {
                color: (t) => `${t.palette.action.disabled} !important`,
              },

              "--DataGrid-rowBorderColor": "transparent",

              "& .MuiDataGrid-filler": { display: "none" },

              "& .MuiDataGrid-columnHeaders": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                color: "text.primary",
                borderBottom: "none",
              },

              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold !important",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              },

              "& .MuiDataGrid-columnSeparator": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-columnHeader .MuiSvgIcon-root:not(.MuiCheckbox-root .MuiSvgIcon-root)": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-cell": {
                color: "text.primary",
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-row:nth-of-type(even)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#fafafa",
              },

              "& .MuiDataGrid-row:nth-of-type(odd)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "transparent" : "#ffffff",
              },

              "& .MuiDataGrid-row:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08) !important" : "#e8f0fe !important",
              },

              "& .MuiDataGrid-row.Mui-selected": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.16) !important" : "#e3f2fd !important",
              },

              "& .MuiDataGrid-row.Mui-selected:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.24) !important" : "#d0eafb !important",
              },

              "& .MuiDataGrid-footerContainer": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                borderTop: "none",
                color: "text.primary",
              },

              "& .floating-actions, & .row-actions": {
                backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(40, 40, 40, 0.9)" : "#e8f0fe",
                flex: 1,
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                gap: "6px",
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity 0.2s ease-in-out",
                padding: "4px 8px",
                borderRadius: "4px",
              },

              "& .MuiDataGrid-row:hover .floating-actions, & .MuiDataGrid-row:hover .row-actions, & .MuiDataGrid-row.Mui-selected .floating-actions, & .MuiDataGrid-row.Mui-selected .row-actions": {
                opacity: 1,
                pointerEvents: "auto",
              },

              "& .MuiDataGrid-cell[data-field='__floating_actions'], & .MuiDataGrid-cell[data-field='actions']": {
                overflow: "visible !important",
                width: "0px !important",
                padding: 0,
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                outline: "none"
              },
            }}
          />
        )}
      </Box>

      {/* Reusable External Popup Component */}
      <SetupDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        setupData={selectedSetupData}
        onSuccess={fetchSetupDetails}
      />
    </Box>
  );
}